import './QuestionForm.css';
import { useState, useEffect } from 'react';
import { searchBooks } from '../../../services/bookService';
import { searchAuthors } from '../../../services/authorService';

function TimelineForm({ onSubmit, onCancel }) {
    const [questionText, setQuestionText] = useState('');
    const [items, setItems] = useState([
        { id: 'item-1', text: '', order: 1 },
        { id: 'item-2', text: '', order: 2 },
        { id: 'item-3', text: '', order: 3 }
    ]);

    // Combined search for books and authors
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchResults, setSearchResults] = useState({ books: [], authors: [] });
    const [selectedItems, setSelectedItems] = useState([]); // Array of { type: 'book'|'author', data: {...} }
    const [searching, setSearching] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Search both books and authors
    useEffect(() => {
        const performSearch = async () => {
            if (debouncedSearch.trim().length < 2) {
                setSearchResults({ books: [], authors: [], showUnlinked: false });
                return;
            }

            try {
                setSearching(true);

                const term = debouncedSearch.trim();
                const bookFilters = { title: term, authorName: term };
                const [bookResults, authorResults] = await Promise.all([
                    searchBooks(bookFilters, 0, 5),
                    searchAuthors(term, 0, 5)
                ]);
                const showUnlinked = 'literární teorie'.includes(term.toLowerCase());

                setSearchResults({
                    books: bookResults.content || [],
                    authors: authorResults.content || [],
                    showUnlinked
                });
            } catch (err) {
                console.error('Error searching:', err);
                setSearchResults({ books: [], authors: [], showUnlinked: false });
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearch]);

    const handleItemChange = (index, value) => {
        const newItems = [...items];
        newItems[index].text = value;
        setItems(newItems);
    };

    const handleAddItem = () => {
        const newId = `item-${items.length + 1}`;
        const newOrder = items.length + 1;
        setItems([...items, { id: newId, text: '', order: newOrder }]);
    };

    const handleRemoveItem = (index) => {
        if (items.length <= 2) {
            alert('Časová osa musí mít alespoň 2 položky');
            return;
        }
        const newItems = items.filter((_, i) => i !== index);
        // Re-index orders
        newItems.forEach((item, idx) => {
            item.order = idx + 1;
        });
        setItems(newItems);
    };

    const handleMoveUp = (index) => {
        if (index === 0) return;
        const newItems = [...items];
        // Swap items
        [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
        // Update orders
        newItems.forEach((item, idx) => {
            item.order = idx + 1;
        });
        setItems(newItems);
    };

    const handleMoveDown = (index) => {
        if (index === items.length - 1) return;
        const newItems = [...items];
        // Swap items
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        // Update orders
        newItems.forEach((item, idx) => {
            item.order = idx + 1;
        });
        setItems(newItems);
    };

    const handleSelectBook = (book) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'book' && item.data.id === book.id
        );
        if (alreadySelected) {
            alert('Tato kniha je již vybrána');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'book', data: book }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectAuthor = (author) => {
        const alreadySelected = selectedItems.some(
            item => item.type === 'author' && item.data.id === author.id
        );
        if (alreadySelected) {
            alert('Tento autor je již vybrán');
            return;
        }
        setSelectedItems([...selectedItems, { type: 'author', data: author }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleSelectUnlinked = () => {
        const alreadySelected = selectedItems.some(item => item.type === 'unlinked');
        if (alreadySelected) return;
        setSelectedItems([...selectedItems, { type: 'unlinked' }]);
        setSearchTerm('');
        setSearchResults({ books: [], authors: [], showUnlinked: false });
    };

    const handleRemoveSelected = (index) => {
        setSelectedItems(selectedItems.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!questionText.trim()) {
            alert('Zadejte znění otázky');
            return;
        }

        const filledItems = items.filter(item => item.text.trim());
        if (filledItems.length < 2) {
            alert('Zadejte alespoň 2 události');
            return;
        }

        const questionData = {
            type: 'Timeline',
            questionText: questionText.trim(),
            items: filledItems.map(item => ({
                id: item.id,
                text: item.text.trim()
            })),
            correctOrder: filledItems.map(item => item.id),
            relatedBooks: selectedItems.filter(item => item.type === 'book').map(item => item.data.id),
            relatedAuthors: selectedItems.filter(item => item.type === 'author').map(item => item.data.id)
        };

        onSubmit(questionData);
    };

    return (
        <form className="question-form" onSubmit={handleSubmit}>
            {/* Question Text */}
            <div className="form-group">
                <label htmlFor="questionText" className="form-label required">
                    Znění otázky
                </label>
                <textarea
                    id="questionText"
                    className="form-textarea"
                    rows="3"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Zadejte text otázky (např. 'Seřaďte následující události chronologicky')..."
                    required
                />
            </div>

            {/* Timeline Items */}
            <div className="form-group">
                <label className="form-label required">Události v chronologickém pořadí</label>
                <p className="form-hint" style={{ marginBottom: '1rem' }}>
                    Zadejte události od nejstarší po nejnovější. Použijte šipky k přesunutí položek.
                </p>
                <div className="choices-list">
                    {items.map((item, index) => (
                        <div key={item.id} className="choice-item">
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.25rem',
                                marginRight: '0.5rem',
                                minWidth: '60px'
                            }}>
                                <button
                                    type="button"
                                    className="btn-move"
                                    onClick={() => handleMoveUp(index)}
                                    disabled={index === 0}
                                    title="Posunout nahoru"
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '0.9rem',
                                        backgroundColor: index === 0 ? '#1a1a2e' : '#0f3460',
                                        color: index === 0 ? '#666' : '#e94560',
                                        border: '1px solid #1a1a2e',
                                        borderRadius: '4px',
                                        cursor: index === 0 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    ↑
                                </button>
                                <button
                                    type="button"
                                    className="btn-move"
                                    onClick={() => handleMoveDown(index)}
                                    disabled={index === items.length - 1}
                                    title="Posunout dolů"
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '0.9rem',
                                        backgroundColor: index === items.length - 1 ? '#1a1a2e' : '#0f3460',
                                        color: index === items.length - 1 ? '#666' : '#e94560',
                                        border: '1px solid #1a1a2e',
                                        borderRadius: '4px',
                                        cursor: index === items.length - 1 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    ↓
                                </button>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '40px',
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                color: '#e94560'
                            }}>
                                {index + 1}.
                            </div>
                            <input
                                type="text"
                                className="choice-input"
                                value={item.text}
                                onChange={(e) => handleItemChange(index, e.target.value)}
                                placeholder={`Událost ${index + 1}`}
                            />
                            {items.length > 2 && (
                                <button
                                    type="button"
                                    className="btn-remove-choice"
                                    onClick={() => handleRemoveItem(index)}
                                    title="Odebrat událost"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                {items.length < 10 && (
                    <button
                        type="button"
                        className="btn-add-choice"
                        onClick={handleAddItem}
                    >
                        + Přidat událost
                    </button>
                )}
            </div>

            {/* Related Books and Authors */}
            <div className="form-group">
                <label className="form-label">Související knihy a autoři (nepovinné)</label>

                {selectedItems.length > 0 && (
                    <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {selectedItems.map((item, index) => (
                            <div key={index} className="selected-item">
                                <div className="selected-item-info">
                                    <span className="selected-item-type">
                                        {item.type === 'book' ? 'Kniha' : item.type === 'unlinked' ? 'Kategorie' : 'Autor'}
                                    </span>
                                    <span className="selected-item-title">
                                        {item.type === 'book'
                                            ? item.data.bookTitle
                                            : item.type === 'unlinked'
                                            ? 'Literární teorie'
                                            : item.data.name}
                                    </span>
                                    {item.type === 'book' && item.data.author?.name && (
                                        <span className="selected-item-subtitle">
                                            {item.data.author.name}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-remove-selected"
                                    onClick={() => handleRemoveSelected(index)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    type="text"
                    className="form-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Vyhledat knihu nebo autora..."
                />
                {searching && <p className="search-loading">Hledám...</p>}
                {(searchResults.books.length > 0 || searchResults.authors.length > 0 || searchResults.showUnlinked) && (
                    <div className="search-results">
                        {searchResults.showUnlinked && (
                            <div
                                className="search-result-item"
                                onClick={handleSelectUnlinked}
                            >
                                <span className="result-title">Literární teorie</span>
                                <span className="result-subtitle">Otázky o literární teorii</span>
                            </div>
                        )}
                        {searchResults.books.length > 0 && (
                            <>
                                <div className="search-results-section-header">Knihy</div>
                                {searchResults.books.map(book => (
                                    <div
                                        key={`book-${book.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectBook(book)}
                                    >
                                        <span className="result-title">{book.bookTitle}</span>
                                        <span className="result-subtitle">{book.author?.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                        {searchResults.authors.length > 0 && (
                            <>
                                <div className="search-results-section-header">Autoři</div>
                                {searchResults.authors.map(author => (
                                    <div
                                        key={`author-${author.id}`}
                                        className="search-result-item"
                                        onClick={() => handleSelectAuthor(author)}
                                    >
                                        <span className="result-title">{author.name}</span>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button type="submit" className="btn-submit">
                    Vytvořit otázku
                </button>
                <button type="button" className="btn-cancel-form" onClick={onCancel}>
                    Zrušit
                </button>
            </div>
        </form>
    );
}

export default TimelineForm;
